/**
 * Reads every GraphQL error off an Apollo-style rejection, from both the
 * places they can hide: `graphQLErrors` for a 200 carrying errors, and
 * `networkError.result.errors` for a non-2xx response Apollo classifies as a
 * `ServerError` (same two paths as `resolveScenarioAccessDenial`).
 *
 * Shared by the scenario mutation classifiers (SE-8344 claim, SE-8353
 * remove) so each matches its own contract on the same envelope.
 * @module features/squad-planning/utils/collectGraphQLErrors
 */

/** Minimal shape of the GraphQL errors Apollo attaches to a rejection. */
export interface GraphQLErrorLike {
  readonly message?: unknown;
  readonly extensions?: {
    readonly code?: unknown;
    readonly originalError?: { readonly message?: unknown };
  };
}

/**
 * Collects every GraphQL error found on the rejection.
 * @param error - The value the mutation failed with
 * @returns Every GraphQL error found, in the order encountered
 */
export function collectGraphQLErrors(
  error: unknown
): readonly GraphQLErrorLike[] {
  if (typeof error !== "object" || error === null) return [];
  const direct = (error as { graphQLErrors?: unknown }).graphQLErrors;
  const networkError = (error as { networkError?: unknown }).networkError;
  const result =
    typeof networkError === "object" && networkError !== null
      ? (networkError as { result?: unknown }).result
      : undefined;
  const nested =
    typeof result === "object" && result !== null
      ? (result as { errors?: unknown }).errors
      : undefined;
  return [
    ...(Array.isArray(direct) ? (direct as readonly GraphQLErrorLike[]) : []),
    ...(Array.isArray(nested) ? (nested as readonly GraphQLErrorLike[]) : []),
  ];
}
